/*
 * Copyright (C) 2025-2026 The Klangmotor Authors (see AUTHORS.MD)
 * SPDX-License-Identifier: AGPL-3.0-or-later
 */

package io.peekandpoke.klang.ui.codemirror

import io.peekandpoke.klang.codemirror.ext.Command
import io.peekandpoke.klang.codemirror.ext.Diagnostic
import io.peekandpoke.klang.codemirror.ext.EditorState
import io.peekandpoke.klang.codemirror.ext.EditorStateConfig
import io.peekandpoke.klang.codemirror.ext.EditorView
import io.peekandpoke.klang.codemirror.ext.EditorViewConfig
import io.peekandpoke.klang.codemirror.ext.Extension
import io.peekandpoke.klang.codemirror.ext.MinimapConfig
import io.peekandpoke.klang.codemirror.ext.autocompletion
import io.peekandpoke.klang.codemirror.ext.forEachDiagnostic
import io.peekandpoke.klang.codemirror.ext.forceLinting
import io.peekandpoke.klang.codemirror.ext.javascript
import io.peekandpoke.klang.codemirror.ext.lintGutter
import io.peekandpoke.klang.codemirror.ext.linter
import io.peekandpoke.klang.codemirror.ext.showMinimap
import io.peekandpoke.klang.common.logging.LogArea
import io.peekandpoke.klang.common.logging.Logger
import io.peekandpoke.klang.script.KlangScriptLibrary
import io.peekandpoke.klang.script.intel.toCodeMirrorSeverity
import io.peekandpoke.klang.script.intel.toOffsets
import io.peekandpoke.klang.script.types.KlangSymbol
import io.peekandpoke.klang.ui.HoverPopupCtrl
import io.peekandpoke.klang.workshop.keys.KeyPlatform
import io.peekandpoke.klang.workshop.keys.Keymap
import io.peekandpoke.klang.workshop.keys.KeysManager
import io.peekandpoke.kraft.components.Component
import io.peekandpoke.kraft.components.ComponentRef
import io.peekandpoke.kraft.components.Ctx
import io.peekandpoke.kraft.components.comp
import io.peekandpoke.kraft.components.getAttributeRecursiveOrNull
import io.peekandpoke.kraft.popups.PopupsManager
import io.peekandpoke.kraft.utils.jsObject
import io.peekandpoke.kraft.vdom.VDom
import io.peekandpoke.ultra.common.OnChange
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.html.FlowContent
import kotlinx.html.Tag
import kotlinx.html.div
import kotlinx.html.id
import org.w3c.dom.Element
import org.w3c.dom.HTMLDivElement
import org.w3c.dom.events.Event
import org.w3c.dom.events.MouseEvent

private val log = Logger(area = LogArea.Editor, source = "CodeMirror")

/** Backward-compatible alias. */
@Suppress("unused")
typealias CodeMirrorComp = KlangScriptEditorComp

@Suppress("FunctionName")
fun Tag.KlangScriptEditorComp(
    code: String,
    onCodeChanged: OnChange<String>,
    availableLibraries: List<KlangScriptLibrary> = emptyList(),
    autoImportedLibraries: List<KlangScriptLibrary> = emptyList(),
    hoverPopup: HoverPopupCtrl? = null,
    hoverContent: (FlowContent.(symbol: KlangSymbol, declarationCode: String?) -> Unit)? = null,
    popups: PopupsManager? = null,
    onNavigate: ((doc: KlangSymbol, event: dynamic) -> Unit)? = null,
    onShowTool: ((pos: Int) -> Unit)? = null,
    hoverDocs: () -> Boolean = { true },
    onQuickDocumentation: QuickHelpHandler? = null,
    onHoverDocumentation: QuickHelpHandler? = null,
    onToolCall: ((ToolCallSite?) -> Unit)? = null,
    showMinimap: Boolean = false,
    bookmarks: BookmarkOptions? = null,
): ComponentRef<KlangScriptEditorComp> = comp(
    KlangScriptEditorComp.Props(
        code = code,
        onCodeChanged = onCodeChanged,
        availableLibraries = availableLibraries,
        autoImportedLibraries = autoImportedLibraries,
        hoverPopup = hoverPopup,
        hoverContent = hoverContent,
        popups = popups,
        onNavigate = onNavigate,
        onShowTool = onShowTool,
        hoverDocs = hoverDocs,
        onQuickDocumentation = onQuickDocumentation,
        onHoverDocumentation = onHoverDocumentation,
        onToolCall = onToolCall,
        showMinimap = showMinimap,
        bookmarks = bookmarks,
    )
) {
    KlangScriptEditorComp(it)
}

class KlangScriptEditorComp(ctx: Ctx<Props>) : Component<KlangScriptEditorComp.Props>(ctx) {

    //  PROPS  //////////////////////////////////////////////////////////////////////////////////////////////////

    data class Props(
        val code: String,
        val onCodeChanged: OnChange<String>,
        val availableLibraries: List<KlangScriptLibrary> = emptyList(),
        val autoImportedLibraries: List<KlangScriptLibrary> = emptyList(),
        val hoverPopup: HoverPopupCtrl? = null,
        /** Renders the hover popup for a symbol; `declarationCode` is a local's declaration, null for a library name. */
        val hoverContent: (FlowContent.(symbol: KlangSymbol, declarationCode: String?) -> Unit)? = null,
        val popups: PopupsManager? = null,
        val onNavigate: ((doc: KlangSymbol, event: dynamic) -> Unit)? = null,
        /** A right-click's "Open the Sprudel tool" (KLANG-00374): the cursor is put there, the host opens the tab. */
        val onShowTool: ((pos: Int) -> Unit)? = null,
        /** Asked on every move whether the hover shows a name's quick help (KLANG-00349); read live. */
        val hoverDocs: () -> Boolean = { true },
        /** Gets the name at the cursor on quick documentation (Alt+Q) instead of the popup; null for the popup. */
        val onQuickDocumentation: QuickHelpHandler? = null,
        /** Gets the name the mouse rests on instead of the popup, for a help of the host's; null for the popup. */
        val onHoverDocumentation: QuickHelpHandler? = null,
        /**
         * Gets the call around the cursor whose tools a panel of the host's shows (KLANG-00374), after each move and
         * edit; null outside any such call. Null leaves the editor without it.
         */
        val onToolCall: ((ToolCallSite?) -> Unit)? = null,
        /**
         * Shows a minimap of the whole document on the right. The editor then scrolls itself
         * (`height: 100%`), so the host must give it a definite height.
         */
        val showMinimap: Boolean = false,
        /**
         * The numbered bookmarks (KLANG-00323) and the host that keeps them; read once, when the editor is built.
         * Null leaves the editor without bookmarks, and their keys reach the browser.
         */
        val bookmarks: BookmarkOptions? = null,
    )

    //  STATE  //////////////////////////////////////////////////////////////////////////////////////////////////

    private val editorId = "codemirror-editor-${hashCode()}"
    private var editor: EditorView? by value(null)

    /** The underlying CodeMirror EditorView, exposed so the main app can attach external features (e.g. highlight buffer). */
    val editorView: EditorView? get() = editor

    private val theme = CodeMirrorTheme()

    /** The editor's own key map, for a host without a key manager (a test, another app). */
    private val ownKeymap by lazy { Keymap(platform = KeyPlatform.detect(), actions = KlangEditorKeyBindings.actions) }

    /**
     * Runtime errors from the last compile or play attempt, published by [setErrors].
     *
     * Held rather than dispatched: the linter source is the SINGLE writer of the lint state (see
     * [lintDiagnostics]), so runtime errors and analyzer squiggles cannot overwrite each other.
     */
    private var runtimeErrors: List<EditorError> = emptyList()

    /**
     * Set whenever the linter's answer could have changed, cleared by [lintDiagnostics]: tells the
     * lint plugin a re-run is due even though the document did not change.
     *
     * Two things move independently of the document here: the runtime errors [setErrors] hands us,
     * and the debounced analysis behind [EditorDocContext.lastAnalysis].
     */
    private var lintRefreshPending = false

    /** The analyzer diagnostics of the last run, kept so a stale analysis changes nothing. */
    private var analyzerCache: Array<Diagnostic> = emptyArray()

    /** Import-aware documentation context — owns hover docs + completion data. */
    private val docContext = EditorDocContext(
        availableLibraries = props.availableLibraries,
        autoImportedLibraries = props.autoImportedLibraries,
    ).also { it.processCodeImmediate(props.code) }

    init {
        lifecycle {
            onMount {
                initialize()
            }

            onUnmount {
                destroy()
            }
        }
    }

    private fun initialize() {
        val container = dom?.querySelector("#$editorId") as? HTMLDivElement ?: return

        // Set up callback for editor changes
        val updateFn = { update: dynamic ->
            if (update.docChanged) {
                val newCode = update.state.doc.toString()
                docContext.onCodeChanged(newCode)
                props.onCodeChanged(newCode)
            }
        }

        // Create update listener extension
        val updateListenerExtension = EditorView.updateListener.of(updateFn)

        // The analysis is debounced, so it lands after the keystroke that caused it, and after the
        // lint run that the same keystroke triggers through `setErrors`. Without this wake-up the
        // linter would only ever see the analysis of the PREVIOUS keystroke, and a run that
        // happened to read a fresh one would be pure luck.
        docContext.onAnalysisUpdated = { requestLintRun() }

        // Create a linter extension with autoPanel
        val linterSource: (EditorView) -> Array<Diagnostic> = { view -> lintDiagnostics(view) }
        val linterConfig = jsObject<dynamic> {
            autoPanel = true
            // The source depends on more than the document: on the cached analysis, and on the
            // runtime errors [setErrors] hands it. Without this the lint plugin would only ever
            // schedule a run on a document change.
            needsRefresh = { _: dynamic -> lintRefreshPending }
        }
        val linterExtension = linter(linterSource, linterConfig)

        // Build DSL extensions (hover, completion, go to definition) if libraries are available
        val dsl = buildDsl()

        // The setup without CodeMirror's keymaps, the key map's keys (KLANG-00325), then our extensions
        val allExtensions = arrayOf<Any>(
            *EditorKeyTable.setup(),
            EditorKeyTable.extension(currentKeymap = ::editorKeymap, commands = buildKeyCommands(dslCommands = dsl.commands)),
            theme.extension,
            javascript(),
            updateListenerExtension,
            EditPlacesExtension.extension,
            linterExtension,
            lintGutter(),
            *dsl.extensions.toTypedArray(),
            *buildMinimapExtensions().toTypedArray(),
            *buildBookmarkExtensions().toTypedArray(),
        ).unsafeCast<Array<Extension>>()

        // Create editor state config
        val stateConfig = jsObject<EditorStateConfig> {
            this.doc = props.code
            this.selection = null
            this.extensions = allExtensions
        }

        // Create editor state
        val state = EditorState.create(stateConfig).unsafeCast<EditorState>()

        // Create editor view config
        val viewConfig = jsObject<EditorViewConfig> {
            this.state = state
            this.parent = container
            this.root = null
            this.dispatch = null
        }

        // Create editor view
        try {
            val view = EditorView(viewConfig)
            editor = view
        } catch (e: Throwable) {
            log.error("Error initializing CodeMirror", e)
        }

        // Clicking an entry in the lint panel places the cursor at the START of
        // the error range and focuses the editor. The panel's own click handler
        // selects the whole range and keeps focus in its list, and the timeout runs
        // after it so our cursor/focus wins.
        //
        // The position comes from the selection the panel just made, not from a lookup of our
        // own: analyzer diagnostics are repetitive by construction ("unknown argument name" on
        // ten lines), so any key built from the message text sends every one of them to the
        // first match. The panel knows which entry was clicked; we only collapse its range.
        val panelClickListener: (Event) -> Unit = { event ->
            val diagEl = (event.target as? Element)?.closest(".cm-panel-lint .cm-diagnostic")
            if (diagEl != null) {
                window.setTimeout({
                    editor?.let { v ->
                        val from = v.state.selection.main.from
                        v.dispatch(jsObject<dynamic> {
                            this.selection = jsObject<dynamic> { this.anchor = from }
                            this.scrollIntoView = true
                        })
                        v.asDynamic().focus()
                    }
                }, 0)
            }
        }
        container.addEventListener("click", panelClickListener)
    }

    /** The minimap extension when [Props.showMinimap] is set, nothing otherwise. */
    private fun buildMinimapExtensions(): List<Extension> {
        if (!props.showMinimap) {
            return emptyList()
        }

        return listOf(klangMinimap())
    }

    /**
     * The commands of the editor's keys: the table's, back to the last edit, the bookmarks' when it has them, and the
     * DSL's ([dslCommands]: go to definition, quick documentation) when it knows libraries.
     */
    private fun buildKeyCommands(dslCommands: Map<String, List<Command>>): Map<String, List<Command>> =
        EditorKeyTable.commands + EditPlacesExtension.commands + dslCommands +
            (props.bookmarks?.let { BookmarksExtension.commands(it) } ?: emptyMap())

    /**
     * The key map the editor's keys are read from, on every key: the app's, with the user's keys; without a key manager
     * (or one that does not know the editor's keys) the editor's own presets.
     */
    private fun editorKeymap(): Keymap =
        EditorKeyTable.keymapOf(app = getAttributeRecursiveOrNull(KeysManager.key)?.keymap?.invoke(), own = ownKeymap)

    /** The bookmarks when [Props.bookmarks] is set, nothing otherwise. */
    private fun buildBookmarkExtensions(): List<Extension> {
        val options = props.bookmarks ?: return emptyList()

        return listOf(BookmarksExtension.extension(options))
    }

    /** The DSL-aware extensions and the commands of their keys. */
    private class DslParts(val extensions: List<Extension>, val commands: Map<String, List<Command>>)

    /** Builds DSL-aware extensions (hover docs, code completion, go to definition) when libraries are configured. */
    private fun buildDsl(): DslParts {
        if (props.availableLibraries.isEmpty()) return DslParts(extensions = emptyList(), commands = emptyMap())

        val extensions = mutableListOf<Extension>()
        var commands = emptyMap<String, List<Command>>()

        val hoverPopup = props.hoverPopup
        val hoverContent = props.hoverContent
        val popups = props.popups

        // Hover docs + context menu (with the Sprudel tool's item)
        if (hoverPopup != null && hoverContent != null && popups != null) {
            val wiring = dslEditorExtension(
                docProvider = { docContext.docProvider(it) },
                analysisProvider = { docContext.lastAnalysis },
                hoverPopup = hoverPopup,
                hoverContent = hoverContent,
                popups = popups,
                onNavigate = props.onNavigate ?: { _, _ -> },
                onShowTool = props.onShowTool,
                hoverDocs = { props.hoverDocs() },
                onQuickDocumentation = props.onQuickDocumentation,
                onHoverDocumentation = props.onHoverDocumentation,
            )

            extensions.add(wiring.extension)
            commands = wiring.commands
        }

        // The call around the cursor, for the host's tool panel
        props.onToolCall?.let { onToolCall ->
            extensions.add(
                ToolCallFollower(
                    docProvider = { docContext.docProvider(it) },
                    analysisProvider = { docContext.lastAnalysis },
                    onToolCall = onToolCall,
                ).extension
            )
        }

        // Code completion
        extensions.add(
            autocompletion(jsObject {
                this.override = arrayOf(dslCompletionSource(docContext))
                this.activateOnTyping = true
            })
        )

        return DslParts(extensions = extensions, commands = commands)
    }

    fun destroy() {
        editor?.destroy()
    }

    /**
     * Replaces the whole document with [newCode]: a reset to the original code (the song page's, a code example's).
     *
     * Does nothing when the document already is [newCode]. A replacement empties the edit places (KLANG-00322) and
     * keeps the bookmarks on their line numbers, as a reload would (KLANG-00323).
     */
    fun setCode(newCode: String) {
        val view = editor ?: return

        if (view.state.doc.toString() == newCode) return

        view.dispatch(
            view.state.update(
                jsObject {
                    this.changes = jsObject<dynamic> {
                        this.from = 0
                        this.to = view.state.doc.length
                        this.insert = newCode
                    }
                    this.effects = arrayOf(EditPlacesExtension.clear.of(Unit), BookmarksExtension.keepLines.of(Unit))
                }
            )
        )
    }

    // ── Diagnostics ─────────────────────────────────────────────────────────

    /**
     * Linter source, and the ONLY writer of the `@codemirror/lint` state.
     *
     * `setDiagnostics` replaces the whole diagnostic set rather than merging into it, so a second
     * writer silently deletes the first one's markers. [setErrors] used to be that second writer:
     * pressing Play published an empty set with no document change, which wiped every analyzer
     * squiggle, and the lint plugin only reschedules on a document change, so they stayed gone
     * until the next keystroke. Both halves are merged here instead.
     *
     * CodeMirror runs this on its own delay after a document change, so it reads whatever analysis
     * [EditorDocContext] holds at that moment: none before the first parse, and possibly a stale
     * one, since the parse is debounced and a failed parse keeps the last good AST on purpose.
     *
     * Wrapped because a source that throws produces no diagnostics at all. `@codemirror/lint`
     * hands the failure to `logException` rather than letting it escape, so the editor survives
     * either way, but the console line here says which of our two halves broke.
     */
    private fun lintDiagnostics(view: EditorView): Array<Diagnostic> {
        // Cleared first, so a throw below cannot leave `needsRefresh` permanently true and
        // re-run the source on every transaction from here on.
        lintRefreshPending = false

        return try {
            analyzerDiagnostics(view) + runtimeErrorDiagnostics(view)
        } catch (e: Throwable) {
            log.error("Error building diagnostics", e)
            emptyArray()
        }
    }

    /**
     * The diagnostics of the cached `AnalyzedAst`, converted to offsets in the live document.
     *
     * `AnalyzedAst.source` is exactly the text its 1-based line/column pairs refer to. When the
     * document has moved on, re-deriving offsets from those pairs MOVES a squiggle that the lint
     * state has already re-mapped correctly through the change. So a stale analysis re-publishes
     * the previous diagnostics at the positions the lint state currently holds for them, which
     * leaves the editor looking exactly as it did.
     */
    private fun analyzerDiagnostics(view: EditorView): Array<Diagnostic> {
        val analysis = docContext.lastAnalysis ?: return remapAnalyzerDiagnostics(view)

        if (analysis.source != view.state.doc.toString()) {
            return remapAnalyzerDiagnostics(view)
        }

        val doc = CodeMirrorLinterDocument(view.state.doc)

        val built = analysis.diagnostics.mapNotNull { diagnostic ->
            val offsets = diagnostic.toOffsets(doc) ?: return@mapNotNull null

            jsObject<Diagnostic> {
                this.from = offsets.from
                this.to = offsets.to
                this.severity = diagnostic.severity.toCodeMirrorSeverity()
                this.message = diagnostic.message
            }
        }.toTypedArray()

        analyzerCache = built

        return built
    }

    /**
     * Re-publishes [analyzerCache] at the positions the lint state currently holds for it.
     *
     * Identity is the key: `forEachDiagnostic` walks the live lint state, which is built from the
     * objects the last run returned, and reports where each of them sits after the document
     * changes that followed. Anything the mapping dropped (its range fell off the end of the
     * document) drops out of the cache with it.
     */
    private fun remapAnalyzerDiagnostics(view: EditorView): Array<Diagnostic> {
        val cached = analyzerCache

        if (cached.isEmpty()) {
            return cached
        }

        val alive = mutableListOf<Diagnostic>()

        forEachDiagnostic(view.state) { diagnostic, from, to ->
            if (cached.any { it === diagnostic }) {
                // Safe to mutate: these objects are about to be handed straight back as the new
                // diagnostic set, replacing the state they were read from.
                diagnostic.from = from
                diagnostic.to = to
                alive.add(diagnostic)
            }
        }

        analyzerCache = alive.toTypedArray()

        return analyzerCache
    }

    /** The runtime errors of the last compile or play attempt, as diagnostics. */
    private fun runtimeErrorDiagnostics(view: EditorView): Array<Diagnostic> {
        return runtimeErrors.mapNotNull { error ->
            try {
                val lineObj = view.state.doc.line(error.line)
                val from = lineObj.from + (error.col - 1)
                val to = from + error.len

                if (from < 0 || to > view.state.doc.length) {
                    log.warn("Diagnostic position out of bounds: from=$from, to=$to, doc.length=${view.state.doc.length}")
                    return@mapNotNull null
                }

                jsObject<Diagnostic> {
                    this.from = from
                    this.to = to
                    this.severity = "error"
                    this.message = error.message
                }
            } catch (e: Throwable) {
                log.error("Error converting EditorError to Diagnostic", e)
                null
            }
        }.toTypedArray()
    }

    /**
     * Set the runtime errors to display in the editor.
     *
     * Stores them and asks the lint plugin to re-run; the markers appear when [lintDiagnostics]
     * merges them with the analyzer diagnostics. Nothing is dispatched into the lint state from
     * here, so an empty list no longer erases the analyzer squiggles along with the errors.
     */
    fun setErrors(errors: List<EditorError>) {
        runtimeErrors = errors
        requestLintRun()
    }

    /**
     * Ask the lint plugin to run because our answer changed while the document did not.
     *
     * Two steps, and both are needed. `forceLinting` only shortcuts a run that is already
     * scheduled, and the lint plugin schedules one on a document change or when the config's
     * `needsRefresh` reports a change. A call here usually comes with no document change at all,
     * so the empty transaction gives the plugin an update to inspect, `needsRefresh` answers for
     * our pending flag, and `forceLinting` then skips the idle delay so the marker shows up now
     * rather than in three quarters of a second.
     */
    private fun requestLintRun() {
        lintRefreshPending = true

        val view = editor ?: return

        try {
            view.dispatch(jsObject<dynamic> {})
            forceLinting(view)
        } catch (e: Throwable) {
            log.error("Error requesting a lint run", e)
        }
    }

    //  IMPL  ///////////////////////////////////////////////////////////////////////////////////////////////////

    override fun VDom.render() {
        div("code-mirror-container") {
            div {
                id = editorId
            }
        }
    }
}

/**
 * The editor's minimap (`@replit/codemirror-minimap`): blocks, its overlay always shown. A press on it starts its drag,
 * never a text selection across the page (the maintainer, 2026-11-02: "when the code is scrolled by dragging the minimap
 * preview, then text selection on the page must be disabled"): the package's own listeners, on its overlay inside this
 * element, still see the press and drive the scroll.
 */
internal fun klangMinimap(): Extension {
    val config = jsObject<MinimapConfig> {
        create = { _ ->
            // A plain object: inside a jsObject lambda `dom` would bind to an outer `dom`
            val holder: dynamic = js("{}")
            val minimap = document.createElement("div")

            minimap.addEventListener("mousedown", { event ->
                if ((event as MouseEvent).button.toInt() == 0) {
                    event.preventDefault()
                }
            })

            holder.dom = minimap
            holder
        }
        displayText = "blocks"
        showOverlay = "always"
    }

    return showMinimap.of(config)
}
